import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { CircularProgress, Snackbar, Alert, Button } from '@mui/material';

import useErrorManager from "../../hooks/useErrorManager";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

import GenericTable from '../Layout/Table/GenericTable';
import PageContainer from '../Layout/PageContainer';
import TextLinkButton from "../Layout/TextLinkButton"
import TextFilter from "../Layout/Table/TextFilter"

import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext'; // Import breadcrumb context


function Networks({fetchRoute = `/net/listall`, disableBreadcrumb = false, customColumns = null}) {
    const [networks, setNetworks] = useState([]);
    const [filterText, setFilterText] = useState("");

    const [loading, setLoading] = useState(true);

    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    const { updateBreadcrumb } = useBreadcrumb(); // Use breadcrumb context to update breadcrumb

    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();
    
    const columns = [
        { id: 'networkId', label: 'Network ID', filtered: true,
             render: (item) => <TextLinkButton action={() =>handleNetSelect(item.networkId)} text={`${item.networkNumber}:${item.networkSerial}`}/>},
        { id: 'networkDescription', label: 'Network Description', filtered: true},
        { id: 'gatewaySerial', label: 'Gateway SN', filtered: true, render: (item) => <TextLinkButton action={() =>handleGatewaySelect(item.gatewayId)} text={item.gatewaySerial}/>},

        { id: 'nodeCount', label: 'Nodes' },
        { id: 'meterCount', label: 'Meters' },
        { id: 'errorCodes', label: 'Status', filtered: true,  render: (item) => renderNetStatus(item.errorCodes) },
        { id: 'lastConnect', label: 'Last Connection',  render: (item) => formatDateTime(item.lastConnect) }
    ];

    useEffect(() => {

        if(!disableBreadcrumb){
            updateBreadcrumb([
                { label: 'Manage Networks'}
            ]);
        }

        const fetchNetworks = async () => {
            try {
                const response = await axiosInstance.get(fetchRoute, {
                    withCredentials: true,
                });
                setNetworks(response.data);
                clearError('fetch');
                setLoading(false);

            } catch (error) {
                setError('fetch',error.message);
                showNotification('Failed to fetch Networks','error')
                setLoading(false);
            }
        };

        fetchNetworks();
    }, []);


    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleNetSelect = async (networkId) => {
        navigate(`/networks/${networkId}`);
    };

    const handleGatewaySelect = async (gatewayId) => {
        navigate(`/gateways/${gatewayId}`);
    };

    const renderNetStatus = (dataType) => {
        const errorCodes = {
            "40": "Failure",
            "136": "Permanent communications error",
            "144": "Temporary communications error"
        };

        const codes = dataType.split(',');

        // Check if the only code is "0"
        if (codes.length === 1 && codes[0] === "0") {
            return "OK";
        }

        const translations = codes.filter(code => code !== "0").map(code => errorCodes[code]);
        // Join the translations with a comma and return the result
        return translations.join(', ');
    };

    const formatDateTime = (isoString) => {
        const date = new Date(isoString);
    
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
    
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
    
        return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
    };

    return (
            <PageContainer>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <TextFilter
                            columns={customColumns || columns}
                            onFilterChange={handleFilterChange}
                        />
                        <GenericTable
                            items={networks}
                            columns={customColumns || columns}
                            filterText={filterText}
                            getKey={(item) => item.networkId}
                        />
                    </>
                )}
            </PageContainer>
    );
}

export default Networks;